import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type {
  CreateResult,
  DeleteResult,
  GetResult,
  ListResult,
  ServiceContext,
  UpdateResult,
} from "@prolarus/shared";
import { apiPost, ApiError } from "@/lib/apiClient";
import {
  tryCreate,
  tryDelete,
  tryGet,
  tryList,
  tryUpdate,
} from "./_shared";

export type InvoiceItem = Tables<"invoice_items">;
export type InvoiceItemInsert = Omit<TablesInsert<"invoice_items">, "company_id">;
export type InvoiceItemUpdate = TablesUpdate<"invoice_items">;

export const invoiceItemService = {
  get(id: string): Promise<GetResult<InvoiceItem>> {
    return tryGet<InvoiceItem>(`/api/invoice-items/${encodeURIComponent(id)}`);
  },

  listByInvoice(invoiceId: string): Promise<ListResult<InvoiceItem>> {
    return tryList<InvoiceItem>(
      `/api/invoice-items/by-invoice/${encodeURIComponent(invoiceId)}`,
    );
  },

  async countByInvoices(invoiceIds: string[]): Promise<{ data: Record<string, number>; error: Error | null }> {
    if (invoiceIds.length === 0) return { data: {}, error: null };
    try {
      const result = await apiPost<{ data: Record<string, number> }>(
        "/api/invoice-items/count-by-invoices",
        { invoice_ids: invoiceIds },
      );
      return { data: result.data, error: null };
    } catch (err) {
      const error = err instanceof ApiError ? err : err instanceof Error ? err : new Error(String(err));
      return { data: {}, error };
    }
  },

  create(data: InvoiceItemInsert, _context: ServiceContext): Promise<CreateResult<InvoiceItem>> {
    return tryCreate<InvoiceItem>("/api/invoice-items", data);
  },

  async createBatch(
    items: InvoiceItemInsert[],
    _context: ServiceContext,
  ): Promise<CreateResult<InvoiceItem[]>> {
    if (items.length === 0) return { data: [], error: null };
    try {
      const result = await apiPost<{ data: InvoiceItem[] }>(
        "/api/invoice-items/batch",
        { items },
      );
      return { data: result.data, error: null };
    } catch (err) {
      const error = err instanceof ApiError ? err : err instanceof Error ? err : new Error(String(err));
      return { data: null, error };
    }
  },

  // snapshot, parentId, currentValues are accepted for backward-compat with
  // the prior service signature. The server fetches its own snapshot, derives
  // the parent invoice_id from it, and recomputes line_total internally.
  update(
    id: string,
    data: InvoiceItemUpdate,
    _snapshot: Record<string, unknown>,
    _parentId: string,
    _context: ServiceContext,
    _currentValues?: { quantity: number | null; unit_price: number | null; discount: number | null; line_total: number | null },
  ): Promise<UpdateResult<InvoiceItem>> {
    return tryUpdate<InvoiceItem>(`/api/invoice-items/${encodeURIComponent(id)}`, data);
  },

  delete(
    id: string,
    _snapshot: Record<string, unknown>,
    _parentId: string,
    _context: ServiceContext,
  ): Promise<DeleteResult> {
    return tryDelete(`/api/invoice-items/${encodeURIComponent(id)}`);
  },

  async deleteBatch(
    ids: string[],
    invoiceId: string,
    _context: ServiceContext,
  ): Promise<DeleteResult> {
    if (ids.length === 0) return { success: true, error: null, changesLogged: false };
    try {
      const result = await apiPost<{ success: boolean; changesLogged?: boolean }>(
        "/api/invoice-items/delete-batch",
        { ids, invoice_id: invoiceId },
      );
      return { success: result.success, error: null, changesLogged: !!result.changesLogged };
    } catch (err) {
      const error = err instanceof ApiError ? err : err instanceof Error ? err : new Error(String(err));
      return { success: false, error, changesLogged: false };
    }
  },

  deleteByInvoice(
    invoiceId: string,
    _context: ServiceContext,
  ): Promise<DeleteResult> {
    return tryDelete(`/api/invoice-items/by-invoice/${encodeURIComponent(invoiceId)}`);
  },
};
